import { useQuery } from "@tanstack/react-query";
import { queryCommonFlags } from "@/api/constants";
import { getLeadTags } from "@/modules/lead-tags/api";
import { useContext, useMemo, useState } from "react";
import { useCallback } from "react";
import { BasePaginationParams } from "@/modules/common/dtos/index.v2";
import { debounce } from "lodash";
import { userHasPermission } from "@/modules/common/utils";
import { PermissionsEnum } from "@/modules/common/types";
import { PermissionLevel } from "@/modules/common/types";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";

const useGetLeadTags = () => {
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const [pagination, setPagination] = useState<BasePaginationParams>({
    page: 1,
    pageSize: 50,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const handleSearchChange = useMemo(
    () =>
      debounce((event) => {
        const newSearchTerm = event?.target?.value || "";
        setSearchTerm(newSearchTerm);
      }, 300),
    []
  );

  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page: page }));
  }, []);

  const {
    data: tags,
    isLoading,
    error,
  } = useQuery(
    ["leadTags", pagination, searchTerm],
    () => getLeadTags(pagination, searchTerm),
    {
      ...queryCommonFlags,
      enabled: userHasPermission(
        {
          name: PermissionsEnum.LEAD_TAGS,
          level: PermissionLevel.READ,
        },
        currentUserPermissions
      ),
      retry: false,
    }
  );
  return {
    tags: tags?.data?.results || [],
    total: tags?.data?.total || 0,
    isLoading,
    isError: !!error,
    handlePageChange,
    pagination,
    handleSearchChange,
    searchTerm,
    setSearchTerm,
  };
};

export default useGetLeadTags;
